<template>
  <div id="fund-group-manager">
    <v-card
      :flat="flat"
      :class="{ 'transparent': flat }"
    >
      <v-container
        :pa-0="noPadding"
        fluid
        grid-list-xl
      >
        <v-row>
          <!-- Search fields and predefined options -->
          <v-col
            cols="12"
            md="4"
          >
            <v-subheader
              v-if="insiders"
              class="pl-0 d-block"
              style="height: auto"
            >
              Search for an insider
            </v-subheader>
            <helpers-section-sub-header v-else>
              Search for a fund
            </helpers-section-sub-header>

            <!-- Autocomplete -->
            <v-row>
              <v-col>
                <v-autocomplete
                  v-model="filerGroupProxy.filers"
                  :search-input.sync="searchInput"
                  :items="searchResults"
                  :loading="loading"
                  :hide-no-data="!searchInput || searchInput.length < 2 || loading"
                  no-data-text="No such fund found"
                  return-object
                  item-text="name"
                  item-value="id"
                  menu-props="offsetY"
                  cache-items
                  multiple
                  dense
                  prepend-inner-icon="search"
                  append-icon=""
                  hide-details
                  class="pt-0"
                >
                  <template
                    slot="selection"
                    slot-scope="selection"
                  />
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title v-html="item.name" />
                      <v-list-item-subtitle v-html="item.description" />
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>

            <!-- Filter filers -->
            <v-row v-if="!insiders">
              <v-col>
                <v-select
                  v-model="filterInput"
                  :items="filtersItems"
                  menu-props="offsetY"
                  hide-details
                  class="pt-0"
                  style="max-width: 175px"
                />
              </v-col>
            </v-row>

            <!-- Predefined options -->
            <v-row v-if="!noPredefined">
              <v-col cols="12">
                <v-subheader
                  v-if="insiders"
                  class="pl-0 mb-2 d-block"
                  style="height: auto"
                >
                  Or choose from:
                </v-subheader>
                <helpers-section-sub-header
                  v-else
                  class="mb-2"
                >
                  Or choose from:
                </helpers-section-sub-header>

                <!-- Your groups -->
                <v-menu offset-y>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      small
                      class="ma-1"
                      v-on="on"
                    >
                      Your Groups
                    </v-btn>
                  </template>
                  <v-list
                    dense
                    class="py-0"
                  >
                    <v-list-item
                      v-for="group in groups"
                      :key="group.id"
                      @click="selectGroup(group)"
                    >
                      <v-list-item-content>
                        <v-list-item-title>{{ group.description }}</v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-chip
                          small
                          color="primary"
                          class="white--text ma-1"
                        >
                          {{ group.filers.length }}
                        </v-chip>
                      </v-list-item-action>
                    </v-list-item>
                  </v-list>
                </v-menu>

                <!-- WW presets -->
                <v-menu
                  v-if="!insiders"
                  offset-y
                >
                  <template v-slot:activator="{ on }">
                    <v-btn
                      small
                      class="ma-1"
                      v-on="on"
                    >
                      WW presets
                    </v-btn>
                  </template>
                  <v-list
                    dense
                    class="py-0"
                  >
                    <v-list-item
                      v-for="wwGroup in wwGroups"
                      :key="wwGroup.id"
                      @click="selectWWGroup(wwGroup)"
                    >
                      <v-list-item-content>
                        <v-list-item-title>{{ wwGroup.description }}</v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-chip
                          small
                          color="primary"
                          class="white--text ma-1"
                        >
                          {{ wwGroup.filers.length }}
                        </v-chip>
                      </v-list-item-action>
                    </v-list-item>
                  </v-list>
                </v-menu>

                <!-- A slot for additional options -->
                <!-- Currently only utilized in Backtester -->
                <slot name="predefined-groups" />
              </v-col>
            </v-row>
          </v-col>

          <!-- Selected funds list -->
          <v-col
            cols="12"
            md="8"
          >
            <v-row no-gutters>
              <v-col grow>
                <helpers-section-sub-header>
                  <!-- Backtester exception -->
                  <template v-if="backtester">
                    <span v-if="backtesterCriteria.heat_map">Heat Map Stocks Being Used</span>
                    <span v-else-if="backtesterCriteria.filer_performance.selected">Dynamic Filer Selection Being Used</span>
                    <span v-else-if="backtesterCriteria.whalescore">WhaleScore Filers Being Used</span>
                    <span v-else-if="backtesterCriteria.crowded_stocks">Unique Trade Selection Being Used</span>
                    <template v-else>
                      Selected funds
                      <span class="grey--text">({{ filerGroupProxy.filers.length }})</span>
                    </template>
                  </template>

                  <template v-else>
                    <!-- Insider backtester exception -->
                    <template v-if="insiders">
                      <v-subheader
                        class="pl-0 d-block"
                        style="height: auto"
                      >
                        Selected insiders
                        <span class="grey--text">({{ filerGroupProxy.filers.length }})</span>
                      </v-subheader>
                    </template>

                    <template v-else>
                      Selected funds
                      <span class="grey--text">({{ filerGroupProxy.filers.length }})</span>
                    </template>
                  </template>
                </helpers-section-sub-header>
              </v-col>
              <v-col
                class="shrink"
                pr-0
              >
                <!-- Clear selection -->
                <v-btn
                  v-if="
                    filerGroupProxy.filers.length ||
                      backtesterCriteria.heat_map ||
                      backtesterCriteria.filer_performance.selected ||
                      backtesterCriteria.whalescore ||
                      backtesterCriteria.crowded_stocks
                  "
                  text
                  color="error"
                  class="ma-0"
                  style="height: auto"
                  @click="clearSelection()"
                >
                  Clear
                </v-btn>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <!-- Backtester exception -->
                <template v-if="backtester">
                  <template v-if="backtesterCriteria.heat_map">
                    <a
                      href="https://whalewisdom.com/report/heat_map"
                      target="_blank"
                      rel="noopener"
                    >Find out more about 13F Heat Map selections</a>
                  </template>

                  <template v-else-if="backtesterCriteria.filer_performance.selected">
                    Dynamic Selection: {{ backtesterCriteria.filer_performance.top_performing_type.description }}
                  </template>

                  <template v-else-if="backtesterCriteria.whalescore">
                    WhaleScore Filers: {{ backtesterCriteria.whalescore.description }}
                  </template>
                  <template v-else-if="backtesterCriteria.crowded_stocks">
                    Unique Trades
                  </template>

                  <span v-else-if="!filerGroupProxy.filers.length">
                    {{ optional ? 'No funds selected.' : 'You must select at least one fund.' }}
                  </span>

                  <template v-else>
                    <v-chip
                      v-for="filer in filerGroupProxy.filers"
                      :key="filer.id"
                      small
                      label
                      close
                      class="ma-1"
                      color="primary white--text"
                      @update:active="removeFundById(filer.id)"
                    >
                      <strong>{{ filer.name }}</strong>
                      <small
                        v-if="filer.last_13f_quarter"
                        class="ml-1"
                      > last 13F: {{ filer.last_13f_quarter }}</small>
                    </v-chip>
                  </template>
                </template>

                <template v-else>
                  <span v-if="!filerGroupProxy.filers.length">
                    <template v-if="insiders">
                      {{ optional ? 'No insiders selected.' : 'You must select at least one insider.' }}
                    </template>
                    <template v-else>
                      {{ optional ? 'No funds selected.' : 'You must select at least one fund.' }}
                    </template>
                  </span>

                  <template v-else>
                    <v-chip
                      v-for="filer in filerGroupProxy.filers"
                      :key="filer.id"
                      small
                      label
                      close
                      class="ma-1"
                      color="primary white--text"
                      @click:close="removeFundById(filer.id)"
                    >
                      <strong>{{ filer.name }}</strong>
                      <small
                        v-if="filer.last_13f_quarter"
                        class="ml-1"
                      > last 13F: {{ filer.last_13f_quarter }}</small>
                    </v-chip>
                  </template>
                </template>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-container>

      <!-- Create/Save this group -->
      <template v-if="!noCreateGroup">
        <v-divider :class="{ 'mt-2': noPadding }" />
        <v-card-actions class="pa-2 justify-end">
          <core-create-this-group
            :filer-group.sync="filerGroupProxy"
            :local="Boolean(filerGroup)"
          />
        </v-card-actions>
      </template>
    </v-card>
  </div>
</template>

<script>
// Modules
import api from '@/store/api'
import cloneDeep from 'lodash/cloneDeep'
import { mapState } from 'vuex'
import { mapFields } from 'vuex-map-fields'

// Static data
import { defaultSelectedGroup } from '@/data/constants'

export default {
  props: {
    flat: Boolean,
    noPadding: Boolean,
    noPredefined: Boolean,
    noCreateGroup: Boolean,
    filerGroup: Object,
    optional: Boolean,
    backtester: Boolean,
    insiders: Boolean,
    whaleTrader: Boolean
  },

  data: () => ({
    loading: false,
    filerGroupProxy: {},
    filterInput: 1,
    filtersItems: [
      { text: 'All Filers', value: 0 },
      { text: '13F Filers Only', value: 1 }
    ],
    searchInput: null,
    searchResults: []
  }),

  computed: {
    backtesterCriteria () {
      return this.whaleTrader ? this.$store.state.app.whaleTraderCriteria.signals.f13_signal : this.$store.state.app.backtesterCriteria
    },
    ...mapState({
      groups: state => state.user.groups,
      wwGroups: state => state.ww.wwGroups,
      selectedGroup: state => state.app.selectedGroup
    }),
    ...mapFields({
      backtesterCriteria2: 'app.backtesterCriteria'
    })
  },

  watch: {
    searchInput (val) {
      val && val.length > 2 && this.querySearch(val)
      this.searchInput = null
    },

    filerGroupProxy: {
      deep: true,
      handler (group) {
        if (this.filerGroup) {
          this.$emit('update:filerGroup', group)
        } else {
          this.$store.commit('app/SET_SELECTED_GROUP', group)
        }
      }
    }
  },

  created () {
    if (this.filerGroup) {
      this.filerGroupProxy = this.filerGroup
    } else {
      this.$watch(
        'selectedGroup',
        group => (this.filerGroupProxy = group),
        { immediate: true }
      )
    }
  },

  methods: {
    querySearch (val) {
      const query = `filer_autocomplete?filer_restrictions=${this.filterInput}&keyword=${val}`

      this.loading = true

      api.get(query)
        .then(res => {
          res.data.forEach(filer => {
            filer.id = parseInt(filer.id)
          })

          this.searchResults = res.data
        })
        .then(() => (this.loading = false))
    },

    removeFundById (id) {
      const index = this.filerGroupProxy.filers.findIndex(filer => filer.id === id)
      this.filerGroupProxy.filers.splice(index, 1)
    },

    selectGroup (group) {
      this.backtesterCriteria.whalescore = null
      this.backtesterCriteria.heat_map = false
      if (typeof this.backtesterCriteria.crowded_stocks !== 'undefined') {
        this.backtesterCriteria.crowded_stocks = false
      }
      this.backtesterCriteria.filer_performance.top_performing_type = {
        description: 'Do not use dynamic fund selection',
        value: 0,
        requires_subscription: true
      }
      this.backtesterCriteria.filer_performance.selected = false

      this.filerGroupProxy = cloneDeep(group)
    },

    selectWWGroup (group) {
      this.backtesterCriteria.whalescore = null
      this.backtesterCriteria.heat_map = false
      if (typeof this.backtesterCriteria.crowded_stocks !== 'undefined') {
        this.backtesterCriteria.crowded_stocks = false
      }
      this.backtesterCriteria.filer_performance.top_performing_type = {
        description: 'Do not use dynamic fund selection',
        value: 0,
        requires_subscription: true
      }
      this.backtesterCriteria.filer_performance.selected = false

      const wwGroup = {
        ...group,
        id: null,
        description: `Copy of ${group.description}`,
        visibility_type_id: 0
      }

      this.filerGroupProxy = cloneDeep(wwGroup)
    },

    clearSelection (skipConfirm) {
      if (!skipConfirm && !window.confirm('Are you sure you want to clear your current fund selection?')) {
        return
      }

      this.filerGroupProxy = cloneDeep(defaultSelectedGroup)
      this.backtesterCriteria.heat_map = false
      if (typeof this.backtesterCriteria.crowded_stocks !== 'undefined') {
        this.backtesterCriteria.crowded_stocks = false
      }
      this.backtesterCriteria.filer_performance.selected = false
      this.backtesterCriteria.filer_performance.top_performing_type = {
        description: 'Do not use dynamic fund selection',
        value: 0,
        requires_subscription: true
      }
      this.backtesterCriteria.whalescore = null
    }
  }
}
</script>
